import { IIndicator, UIErrorHandlingService } from "@gtmhub/error-handling";
import { IIntervalService, IPromise, IScope } from "angular";
import { IDeltaHistorySync, IDeltaRun, IWorkdaySettings, SetupStep, WorkdayMappingRunStatus } from "../models";
import { WorkdayOperationsReportService } from "../workday-operations-report-service";
import { WorkdayUserProvisioningService } from "../workday-user-provisioning-service";

const pollingIntervalMillis = 5000; //5sec

export class WorkdaySettingsCtrl {
  progressSteps = SetupStep;
  indicators: {
    loading?: IIndicator;
    isManualSyncRunning?: IIndicator;
  };
  lastDeltaHistorySync: IDeltaHistorySync;
  workdaySettings: IWorkdaySettings;
  currentSetupStep: SetupStep;
  intervalValue: IPromise<unknown>;
  isSetupCompleted: boolean;

  static $inject = ["$scope", "WorkdayUserProvisioningService", "WorkdayOperationsReportService", "UIErrorHandlingService", "$interval"];
  constructor(
    private $scope: IScope,
    private workdayUserProvisioningService: WorkdayUserProvisioningService,
    private workdayOperationsReportService: WorkdayOperationsReportService,
    private uiErrorHandlingService: UIErrorHandlingService,
    private $interval: IIntervalService
  ) {}

  $onInit(): void {
    this.indicators = {
      loading: { progress: false },
      isManualSyncRunning: { progress: false },
    };
    this.getWorkdaySettings();
    this.getLatestDeltaHistorySyncs();
    this.getCurrentSetupStep();
    this.checkIfLastSyncIsRunning();
    this.$scope.$on("progressStepChanged", () => this.updateCurrentSetupStep());
    this.$scope.$on("$destroy", () => this.clearIntervalValue());
  }

  private getCurrentSetupStep(): void {
    this.setIndicator("loading", true);
    this.workdayUserProvisioningService
      .getCurrentSetupProgressStep()
      .then((setupStep) => {
        this.setCurrentSetupStep(setupStep);
      })
      .catch((error) => {
        // workday settings init creation is done on workday-configuration-ctrl
        if (!error.workdaySettingsDoesNotExists) {
          this.uiErrorHandlingService.handleModal(error);
        }
      })
      .finally(() => {
        this.setIndicator("loading", false);
      });
  }

  private updateCurrentSetupStep(): void {
    this.workdayUserProvisioningService
      .getCurrentSetupProgressStep()
      .then((setupStep) => {
        this.setCurrentSetupStep(setupStep);
      })
      .catch((error) => {
        this.uiErrorHandlingService.handleModal(error);
      });
  }

  private setCurrentSetupStep(setupStep: SetupStep): void {
    this.currentSetupStep = setupStep;
    this.isSetupCompleted = setupStep === this.progressSteps.COMPLETE;
  }

  private checkIfLastSyncIsRunning(): void {
    this.workdayUserProvisioningService.getDeltaRuns({ skip: 0, take: 1 }).then((runs) => {
      const lastDeltaRun = runs[0];
      if (lastDeltaRun?.status === WorkdayMappingRunStatus.Pending || lastDeltaRun?.status === WorkdayMappingRunStatus.Started) {
        this.setIndicator("isManualSyncRunning", true);
        this.checkIfSyncIsRunning(lastDeltaRun);
      } else {
        this.setIndicator("isManualSyncRunning", false);
      }
    });
  }

  private checkIfSyncIsRunning(sync: IDeltaRun): void {
    this.workdayUserProvisioningService
      .getDeltaRun(sync.id)
      .then((deltaRun) => {
        if (deltaRun.status === WorkdayMappingRunStatus.Ended || deltaRun.status === WorkdayMappingRunStatus.Failed) {
          this.getLatestDeltaHistorySyncs();
          this.setIndicator("isManualSyncRunning", false);
          this.clearIntervalValue();
        } else {
          if (!this.intervalValue) {
            this.intervalValue = this.$interval(() => this.checkIfLastSyncIsRunning(), pollingIntervalMillis);
          }
        }
      })
      .catch((error) => {
        this.uiErrorHandlingService.handleModal(error);
      });
  }

  private clearIntervalValue(): void {
    if (this.intervalValue) {
      this.$interval.cancel(this.intervalValue);
      delete this.intervalValue;
    }
  }

  setIndicator(indicator: keyof WorkdaySettingsCtrl["indicators"], progress: boolean): void {
    if (progress) {
      this.indicators[indicator] = { progress };
    } else {
      delete this.indicators[indicator];
    }
  }

  getWorkdaySettings(): void {
    this.workdayUserProvisioningService
      .getWorkdayUserProvisionSettings()
      .then((response) => {
        this.workdaySettings = response;
      })
      .catch((error) => {
        // workday settings init creation is done on workday-configuration-ctrl
        this.uiErrorHandlingService.handleModal(error);
      });
  }

  getLatestDeltaHistorySyncs(): void {
    this.workdayUserProvisioningService
      .getDeltaHistorySyncs()
      .then((response) => {
        if (response.items.length > 0) {
          this.lastDeltaHistorySync = response.items[0];
        }
      })
      .catch((error) => {
        this.uiErrorHandlingService.handleModal(error);
      });
  }

  runManualSync(): void {
    this.setIndicator("isManualSyncRunning", true);
    this.toggleAutoSync(false);
    this.workdayUserProvisioningService
      .runDelta()
      .then(() => {
        this.checkIfLastSyncIsRunning();
      })
      .catch((error) => {
        this.uiErrorHandlingService.handleModal(error);
        this.setIndicator("isManualSyncRunning", false);
      });
  }

  toggleAutoSync(provisioningEnabled: boolean): void {
    this.workdayUserProvisioningService
      .updateWorkdaySettings({ provisioningEnabled: !provisioningEnabled })
      .then(() => {
        this.workdaySettings.provisioningEnabled = !provisioningEnabled;
      })
      .catch((error) => {
        this.uiErrorHandlingService.handleModal(error);
      });
  }

  downloadLastSyncReport(): void {
    if (this.lastDeltaHistorySync) {
      this.workdayOperationsReportService.triggerDownload(this.lastDeltaHistorySync);
    }
  }
}
